// app/components/NavBar.tsx
'use client';

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { isAdminOnlyPage, getNavItemsForRole, isNavAllowed } from '@/lib/rbac';
import { nav, type NavEntry, type NavSection } from '@/lib/nav-config';

function filterNavForRole(role: string, entries: NavEntry[]): NavEntry[] {
  if (role === 'admin') return entries;
  return entries
    .map((entry): NavEntry | null => {
      if (entry.kind === 'link') {
        return isAdminOnlyPage(entry.href) ? null : entry;
      }
      if (entry.kind === 'flat') {
        const items = getNavItemsForRole(role, entry.items);
        return items.length > 0 ? { ...entry, items } : null;
      }
      const sections = entry.sections
        .map((section): NavSection | null => {
          const items = getNavItemsForRole(role, section.items);
          return items.length > 0 ? { ...section, items } : null;
        })
        .filter((s): s is NavSection => s !== null);
      return sections.length > 0 ? { ...entry, sections } : null;
    })
    .filter((e): e is NavEntry => e !== null);
}

// Restrict the (already role-filtered) nav to the hrefs allowed by the user's
// nav template. `allowed === null` means no template → full access.
function filterNavForTemplate(allowed: string[] | null, entries: NavEntry[]): NavEntry[] {
  if (allowed === null) return entries;
  return entries
    .map((entry): NavEntry | null => {
      if (entry.kind === 'link') {
        return isNavAllowed(entry.href, allowed) ? entry : null;
      }
      if (entry.kind === 'flat') {
        const items = entry.items.filter((i) => isNavAllowed(i.href, allowed));
        return items.length > 0 ? { ...entry, items } : null;
      }
      const sections = entry.sections
        .map((section): NavSection | null => {
          const items = section.items.filter((i) => isNavAllowed(i.href, allowed));
          return items.length > 0 ? { ...section, items } : null;
        })
        .filter((s): s is NavSection => s !== null);
      return sections.length > 0 ? { ...entry, sections } : null;
    })
    .filter((e): e is NavEntry => e !== null);
}

const ENTRY_COLORS: Record<string, string> = {
  'Übersicht': '#f5e642',
  'Analyse':   '#42c8f5',
  'Meta Ads':  '#4267f5',
  'Content':   '#f542c8',
  'Planung':   '#f5a042',
  'CRM':       '#42f5da',
  'System':    '#a042f5',
};

function isEntryActive(entry: NavEntry, pathname: string): boolean {
  if (entry.kind === 'link') return pathname === entry.href;
  if (entry.kind === 'flat') return entry.items.some(i => i.href === pathname);
  return entry.sections.some(s => s.items.some(i => i.href === pathname));
}

function getActiveLabel(pathname: string): string {
  for (const entry of nav) {
    if (entry.kind === 'link') {
      if (pathname === entry.href) return entry.label;
    } else if (entry.kind === 'flat') {
      const found = entry.items.find(i => i.href === pathname);
      if (found) return found.label;
    } else {
      for (const s of entry.sections) {
        const found = s.items.find(i => i.href === pathname);
        if (found) return found.label;
      }
    }
  }
  return 'Menü';
}

function getActiveGroupLabel(pathname: string): string | null {
  for (const entry of nav) {
    if (isEntryActive(entry, pathname)) {
      return entry.kind === 'link' ? entry.label : entry.label;
    }
  }
  return null;
}

// Desktop dropdown layout: every section stays one intact block. Sections with
// more than 12 items get their own sub-columns under one heading; small
// sections are stacked into a shared column up to the height of the tallest
// block (heading ≈ 1.5 rows, at least 10 rows).
type NavBlock = { section: NavSection; chunks: NavSection['items'][] };
type NavGroup = { kind: 'wide'; block: NavBlock } | { kind: 'stack'; blocks: NavBlock[] };

function layoutSections(sections: NavSection[]): NavGroup[] {
  const blocks: NavBlock[] = sections.map((section) => {
    const k = section.items.length > 12 ? Math.ceil(section.items.length / 10) : 1;
    const size = Math.ceil(section.items.length / k);
    const chunks = Array.from({ length: k }, (_, i) => section.items.slice(i * size, (i + 1) * size));
    return { section, chunks };
  });
  const rows = (b: NavBlock) => b.chunks[0].length + 1.5;
  const target = Math.max(10, ...blocks.map(rows));
  const groups: NavGroup[] = [];
  let stack: NavBlock[] = [];
  let used = 0;
  const flush = () => { if (stack.length) groups.push({ kind: 'stack', blocks: stack }); stack = []; used = 0; };
  for (const b of blocks) {
    if (b.chunks.length > 1) { flush(); groups.push({ kind: 'wide', block: b }); continue; }
    if (stack.length && used + rows(b) > target) flush();
    stack.push(b);
    used += rows(b);
  }
  flush();
  return groups;
}

const ACTIVE_STYLE = {
  color: '#07110a',
  background: 'linear-gradient(135deg, #6cac53 0%, #8ede7a 100%)',
  fontWeight: 700,
  boxShadow: '0 0 0 1px rgba(108,172,83,0.22), 0 0 18px rgba(108,172,83,0.18)',
} as const;

const INACTIVE_STYLE = {
  color: 'rgba(var(--on-surface),0.78)',
  background: 'transparent',
  fontWeight: 400,
  boxShadow: 'none',
} as const;

export function NavBar() {
  const pathname = usePathname();
  const router = useRouter();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openEntry, setOpenEntry] = useState<string | null>(null);
  const [expandedEntry, setExpandedEntry] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string>('admin');
  const [navAllowed, setNavAllowed] = useState<string[] | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Wide multi-column panels can overflow the viewport — shift them back in.
  useLayoutEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    el.style.transform = '';
    const r = el.getBoundingClientRect();
    const margin = 16;
    let dx = 0;
    if (r.right > window.innerWidth - margin) dx = window.innerWidth - margin - r.right;
    if (r.left + dx < margin) dx = margin - r.left;
    if (dx) el.style.transform = `translateX(${dx}px)`;
  }, [openEntry]);

  useEffect(() => {
    const roleMatch = document.cookie.match(/(?:^|;)\s*kaju_role=([^;]+)/);
    if (roleMatch) setUserRole(decodeURIComponent(roleMatch[1]));

    const navMatch = document.cookie.match(/(?:^|;)\s*kaju_nav=([^;]*)/);
    if (navMatch) {
      const raw = decodeURIComponent(navMatch[1]);
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) setNavAllowed(parsed.filter((x): x is string => typeof x === 'string'));
        } catch { /* malformed cookie → full access */ }
      }
    }
  }, []);

  function handleEnter(label: string) {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenEntry(label);
  }

  function handleLeave() {
    closeTimer.current = setTimeout(() => setOpenEntry(null), 120);
  }

  if (pathname === '/login') return null;
  // Customer-facing pages must not show the internal navigation. ('/review' is
  // an older public page that still does — not touched here.)
  if (pathname.startsWith('/umfrage/') || pathname === '/teilnahmebedingungen') return null;

  async function handleLogout() {
    await fetch('/api/auth/logout', { method: 'POST' });
    router.push('/login');
    router.refresh();
  }

  const filteredNav = filterNavForTemplate(navAllowed, filterNavForRole(userRole, nav));
  const activeGroupLabel = getActiveGroupLabel(pathname);

  return (
    <>
      <nav
        className="kj-nav-bar fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 h-14"
        style={{
          backdropFilter: 'blur(18px)',
          borderBottom: '1px solid rgba(108,172,83,0.14)',
          boxShadow: '0 1px 0 rgba(0,150,174,0.10), 0 8px 24px rgba(0,0,0,0.28)',
        }}
      >
        {/* Logo */}
        <Link href="/dashboard" className="flex items-center gap-2 shrink-0">
          <img
            src="/kaju_logo_thumb.png"
            alt="kajuSPIELBAUSTEINE"
            width={28}
            height={28}
            className="rounded"
            style={{ display: 'block' }}
          />
          <span className="font-bold text-sm tracking-wide hidden sm:inline" style={{ color: 'var(--foreground)' }}>
            kajuSPIELBAUSTEINE
          </span>
        </Link>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-1">
          {filteredNav.map((entry, entryIndex) => {
            const isActive = isEntryActive(entry, pathname);

            if (entry.kind === 'link') {
              return (
                <Link
                  key={entry.href}
                  href={entry.href}
                  className="px-3 py-1.5 rounded text-sm transition-colors"
                  style={isActive ? ACTIVE_STYLE : INACTIVE_STYLE}
                >
                  {entry.label}
                </Link>
              );
            }

            const isOpen = openEntry === entry.label;

            return (
              <div
                key={entry.label}
                className="relative"
                onMouseEnter={() => handleEnter(entry.label)}
                onMouseLeave={handleLeave}
              >
                <button
                  className="px-3 py-1.5 rounded text-sm transition-colors flex items-center gap-1"
                  style={isActive ? ACTIVE_STYLE : INACTIVE_STYLE}
                >
                  {entry.label}
                  <span style={{ fontSize: '0.6rem', opacity: 0.6 }}>{isOpen ? '▲' : '▼'}</span>
                </button>

                {isOpen && (
                  <div
                    ref={panelRef}
                    className={`kj-nav-panel absolute top-full ${entryIndex > filteredNav.length / 2 ? 'right-0' : 'left-0'} rounded-lg z-50`}
                    style={{
                      border: '1px solid rgba(108,172,83,0.18)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                      marginTop: '-2px',
                      maxHeight: 'calc(100vh - 4.5rem)',
                      overflowY: 'auto',
                    }}
                  >
                    {entry.kind === 'flat' ? (
                      <div className="py-1 min-w-[160px]">
                        {entry.items.map(({ href, label }) => {
                          const active = pathname === href;
                          return (
                            <Link
                              key={href}
                              href={href}
                              className="block px-4 py-2 text-sm transition-colors"
                              style={{
                                color: active ? '#6cac53' : 'rgba(var(--on-surface),0.78)',
                                fontWeight: active ? 700 : 400,
                                borderLeft: active ? '2px solid #6cac53' : '2px solid transparent',
                              }}
                            >
                              {label}
                            </Link>
                          );
                        })}
                      </div>
                    ) : (() => {
                      const accent = ENTRY_COLORS[entry.label] ?? '#6cac53';
                      const renderLink = ({ href, label }: { href: string; label: string }) => {
                        const active = pathname === href;
                        return (
                          <Link
                            key={href}
                            href={href}
                            className="block px-3 py-1.5 text-sm transition-colors whitespace-nowrap"
                            style={{
                              color: active ? '#6cac53' : 'rgba(var(--on-surface),0.78)',
                              fontWeight: active ? 700 : 400,
                              borderLeft: active ? '2px solid #6cac53' : '2px solid transparent',
                            }}
                          >
                            {label}
                          </Link>
                        );
                      };
                      const renderBlock = ({ section, chunks }: NavBlock) => (
                        <div key={section.heading} style={{ marginBottom: 10 }}>
                          <div
                            className="mx-3 mb-1 pb-1.5 pt-1"
                            style={{
                              color: 'rgba(var(--on-surface),0.62)',
                              fontSize: '0.66rem',
                              letterSpacing: '0.08em',
                              textTransform: 'uppercase',
                              fontWeight: 700,
                              borderBottom: `1px solid ${accent}55`,
                            }}
                          >
                            {section.heading}
                          </div>
                          <div style={{ display: 'flex' }}>
                            {chunks.map((chunk, ci) => (
                              <div key={ci} style={{ width: 170 }}>{chunk.map(renderLink)}</div>
                            ))}
                          </div>
                        </div>
                      );
                      return (
                        <div style={{ display: 'flex', padding: '10px 4px 0', maxWidth: 'calc(100vw - 2rem)', overflowX: 'auto' }}>
                          {layoutSections(entry.sections).map((g, gi) => (
                            <div
                              key={gi}
                              style={{
                                borderLeft: gi > 0 ? '1px solid rgba(var(--on-surface),0.07)' : 'none',
                                padding: '0 4px',
                              }}
                            >
                              {g.kind === 'wide' ? renderBlock(g.block) : g.blocks.map(renderBlock)}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            );
          })}

          <Link
            href="/dashboard/account"
            className="ml-2 px-3 py-1.5 rounded text-sm transition-opacity hover:opacity-80"
            style={{ color: 'rgba(var(--on-surface),0.45)' }}
          >
            Account
          </Link>
          <button
            onClick={handleLogout}
            className="px-3 py-1.5 rounded text-sm transition-opacity hover:opacity-80"
            style={{ color: 'rgba(var(--on-surface),0.45)' }}
          >
            Logout
          </button>
        </div>

        {/* Mobile: aktive Seite + Burger */}
        <div className="flex md:hidden items-center gap-3">
          <span className="text-sm" style={{ color: 'rgba(var(--on-surface),0.5)' }}>
            {getActiveLabel(pathname)}
          </span>
          <button
            onClick={() => {
              const next = !mobileOpen;
              setMobileOpen(next);
              if (!next) setExpandedEntry(null);
            }}
            className="flex flex-col justify-center items-center w-8 h-8 gap-1.5"
            aria-label="Menü"
          >
            <span
              className="block h-0.5 w-5 transition-all origin-center"
              style={{
                background: 'var(--neutral-100)',
                transform: mobileOpen ? 'translateY(8px) rotate(45deg)' : 'none',
              }}
            />
            <span
              className="block h-0.5 w-5 transition-all"
              style={{ background: 'var(--neutral-100)', opacity: mobileOpen ? 0 : 1 }}
            />
            <span
              className="block h-0.5 w-5 transition-all origin-center"
              style={{
                background: 'var(--neutral-100)',
                transform: mobileOpen ? 'translateY(-8px) rotate(-45deg)' : 'none',
              }}
            />
          </button>
        </div>
      </nav>

      {/* Mobile dropdown */}
      {mobileOpen && (
        <div
          className="kj-nav-panel fixed top-14 left-0 right-0 bottom-0 z-40 md:hidden flex flex-col overflow-y-auto"
        >
          {/* Level-1: alle Einträge im 2-Spalten-Grid mit Neon-Glow */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '8px',
              padding: '12px',
              borderBottom: expandedEntry ? '1px solid rgba(var(--on-surface),0.06)' : 'none',
            }}
          >
            {filteredNav.map((entry) => {
              const isActive = isEntryActive(entry, pathname);
              const label = entry.kind === 'link' ? entry.label : entry.label;
              const color = ENTRY_COLORS[label] ?? '#6cac53';
              const lit = isActive || (entry.kind !== 'link' && expandedEntry === entry.label);

              const neonStyle = {
                color: lit ? color : `${color}cc`,
                fontWeight: lit ? 700 : 400,
                border: `1px solid ${color}${lit ? 'aa' : '44'}`,
                borderRadius: '8px',
                boxShadow: lit
                  ? `0 0 8px ${color}77, 0 0 18px ${color}33`
                  : `0 0 4px ${color}33`,
                background: lit ? `${color}11` : 'transparent',
                padding: '10px 14px',
                fontSize: '0.875rem',
                display: 'block',
                width: '100%',
                textAlign: 'left' as const,
              };

              if (entry.kind === 'link') {
                return (
                  <Link key={entry.href} href={entry.href} onClick={() => setMobileOpen(false)} style={neonStyle}>
                    {entry.label}
                  </Link>
                );
              }

              return (
                <button
                  key={entry.label}
                  onClick={() => setExpandedEntry(expandedEntry === entry.label ? null : entry.label)}
                  style={neonStyle}
                >
                  {entry.label}
                </button>
              );
            })}
          </div>

          {/* Level-2: geöffnetes Dropdown, bildschirmbreit */}
          {expandedEntry && (() => {
            const entry = filteredNav.find(e => e.kind !== 'link' && e.label === expandedEntry);
            if (!entry || entry.kind === 'link') return null;
            return (
              <div style={{ background: 'rgba(var(--on-surface),0.02)', borderBottom: '1px solid rgba(var(--on-surface),0.06)' }}>
                {entry.kind === 'flat' ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }} className="py-1">
                    {entry.items.map(({ href, label }) => {
                      const active = pathname === href;
                      return (
                        <Link
                          key={href}
                          href={href}
                          onClick={() => setMobileOpen(false)}
                          className="block px-6 py-2.5 text-sm"
                          style={{
                            color: active ? '#6cac53' : 'rgba(var(--on-surface),0.65)',
                            fontWeight: active ? 700 : 400,
                          }}
                        >
                          {label}
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  entry.sections.map((section) => (
                    <div key={section.heading}>
                      <div
                        className="px-6 pt-2.5 pb-1"
                        style={{
                          color: 'rgba(var(--on-surface),0.3)',
                          fontSize: '0.6rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          fontWeight: 600,
                        }}
                      >
                        {section.heading}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                        {section.items.map(({ href, label }) => {
                          const active = pathname === href;
                          return (
                            <Link
                              key={href}
                              href={href}
                              onClick={() => setMobileOpen(false)}
                              className="block px-6 py-2 text-sm"
                              style={{
                                color: active ? '#6cac53' : 'rgba(var(--on-surface),0.65)',
                                fontWeight: active ? 700 : 400,
                              }}
                            >
                              {label}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
            );
          })()}

          <div style={{ borderTop: '1px solid rgba(var(--on-surface),0.06)', display: 'flex' }}>
            <Link
              href="/dashboard/account"
              onClick={() => setMobileOpen(false)}
              className="px-6 py-3 text-sm transition-opacity hover:opacity-80"
              style={{ color: 'rgba(var(--on-surface),0.4)' }}
            >
              Account
            </Link>
            <button
              onClick={handleLogout}
              className="px-6 py-3 text-sm text-left transition-opacity hover:opacity-80"
              style={{ color: 'rgba(var(--on-surface),0.4)' }}
            >
              Logout
            </button>
          </div>
        </div>
      )}
    </>
  );
}
